import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subaccount } from '@models/subaccount/subaccount';
import { AccountsSmsAvailability } from '@models/superaccount/settings';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AtlasToastService } from '@services/atlas-toast.service';
import { SubaccountService } from '@services/subaccount.service';
import { SuperaccountService } from '@services/superaccount.service';
import { ToastContainerDirective } from 'ngx-toastr';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getCurrencySymbol } from '@utils/utils';
// import { mTypeRateProfile } from '@models/superaccount/rates-profile';
import { User } from '@models/user';
import { nations } from '@utils/constants';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';

/**
 * Use it like this - https://stackblitz.com/edit/angular-v6amvy-pttrng?file=app%2Fcore%2Fmodal-window%2Fmodal-window.component.html
 * <ng-template #modalMail2smsaddressadd let-c="close" let-d="dismiss"><wsb-modal [primaryButtonFunction]="c" [dismissFunction]="d"
 * title="{{ 'settings_account_mail2sms_add_mail' | translate }}"></wsb-modal></ng-template>
 *   open(content: NgbModalRef) {
 *   this.modalService.open(content).result.then((result) => {
 *     this.closeResult = `Closed with: ${result}`;
 *   }, (reason) => {
 *     this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
 *   });
 * }
 */

// function checkUserCreditsFunds(credits): (AbstractControl) => ValidationErrors | null {
//   return (control: AbstractControl): ValidationErrors | null => {
//     if (control.value) {
//       if (control.value > credits.money) {
//         return { notEnoughCredits: true };
//       }
//     }
//     return null;
//   };
// }

function checkPricePerMessage(funds?): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (funds) {
      if (control.value) {
        if (control.value > funds) {
          return { priceOverFunds: true };
        }
      }
    }
    return null;
  };
}

@Component({
  selector: 'wsb-transfer-credits-modal',
  templateUrl: './transfer-credits-modal.component.html',
  styleUrls: ['./transfer-credits-modal.component.scss'],
  standalone: false
})
export class TransferCreditsModalComponent implements OnInit, OnDestroy {
  @Input() public user: User;
  @Input() public transferType;
  @Input() public subaccount: Subaccount | null;
  @Output() dismiss: EventEmitter<any> = new EventEmitter();
  @Output() primary: EventEmitter<any> = new EventEmitter();
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;

  // pattern for numeric field:
  private pricePattern: RegExp = /^[0-9]\d*(.\d{1,4})?$/;
  private excludeZero: RegExp = /^(?!0*(\.0+)?$)(\d+|\d*\.\d+)$/;
  nations = [];
  faTrash = faTrash;

  smsAvailability: AccountsSmsAvailability;
  smsType: { label: string; value: string }[] = [];
  pricePlan: { label: string; value: number }[] = [];
  pricePlans;
  selectedPricePlan;
  creditsBalanceLabel;

  isUkCurrency = false;
  scrollPage = 1;
  realCreditsForm: UntypedFormGroup;
  virtualCreditsForm: UntypedFormGroup;
  private unsubscribeAll: Subject<any>;

  constructor(
    public activeModal: NgbActiveModal,
    private superaccountService: SuperaccountService,
    private subaccountService: SubaccountService,
    private atlasToast: AtlasToastService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService
  ) {
    this.unsubscribeAll = new Subject();
  }

  ngOnInit() {
    nations.forEach(x => {
      this.nations.push({
        id: x[0],
        label: x[1]
      });
    });
    this.createFormGroup();

    this.setFormValidators();

    this.setPricePerMessageValidators();

    if (getCurrencySymbol() === '£') {
      this.isUkCurrency = true;
    }

    this.superaccountService.getListRatesProfile().subscribe(response => {
      if (response.data) {
        for (const iterator of response.data) {
          this.pricePlan = [
            ...this.pricePlan,
            {
              value: iterator.idRateProfile,
              label: iterator.name
            }
          ];
        }
        if (response.data[0]?.idRateProfile) {
          this.selectedPricePlan = response.data[0].idRateProfile;
        }
      }
      this.initPricePerMessage();
    });
  }

  initPricePerMessage() {
    this.pricePerMessage.clear();
    this.smsType = [];
    this.superaccountService.getSmsAvailability(this.subaccount.idUser).subscribe(response => {
      this.smsAvailability = response;
      this.smsAvailability.superAccount = this.superaccountService.sortPricePerNation(
        this.smsAvailability.superAccount,
        'messageType',
        'idNation',
        this.user.nation
      );
      this.smsAvailability.subAccount = this.superaccountService.sortPricePerNation(
        this.smsAvailability.subAccount,
        'messageType',
        'idNation',
        this.user.nation
      );
      if (this.transferType === 0) {
        for (const iterator of response.superAccount) {
          this.smsType = [
            ...this.smsType,
            {
              value: iterator.messageType,
              label: iterator.messageType
            }
          ];
          this.pricePerMessage.push(this.createPricePerMessage(iterator.messageType, '', false, iterator.idNation));
        }
      }
      if (this.transferType === 2) {
        for (const iterator of response.subAccount) {
          this.smsType = [
            ...this.smsType,
            {
              value: iterator.messageType,
              label: iterator.messageType
            }
          ];
          this.pricePerMessage.push(this.createPricePerMessage(iterator.messageType, '', false, iterator.idNation));
        }
      }

      if (this.selectedPricePlan) {
        this.loadRateProfil();
      } else {
        this.subscribeValueChanges();
      }

      if (this.smsType.length === 1) {
        this.realCreditsForm.get('idMessageType').patchValue(this.smsType[0].value);
      }
    });
  }

  createFormGroup() {
    this.realCreditsForm = this.formBuilder.group({
      idSubaccountUser: [this.subaccount.idUser],
      amount: [],
      idMessageType: [''],
      superTosub: [true]
    });

    this.virtualCreditsForm = this.formBuilder.group({
      idSubaccountUser: [this.subaccount.idUser],
      price: [''],
      pricePerMessage: new UntypedFormArray([], [this.atLeastOneChecked()])
    });
  }

  get pricesPerMessage(): UntypedFormArray {
    return this.virtualCreditsForm.get('pricePerMessage') as UntypedFormArray;
  }

  get price() {
    return this.virtualCreditsForm.get('price').value;
  }

  setPricePerMessageValidators() {
    this.virtualCreditsForm.get('price').valueChanges.subscribe(() => {
      this.pricesPerMessage.controls.forEach(controls => {
        controls
          .get('unitPrice')
          .setValidators([Validators.pattern(this.pricePattern), Validators.pattern(this.excludeZero), checkPricePerMessage(this.price)]);
        controls.get('unitPrice').updateValueAndValidity();
      });
    });
  }

  setFormValidators() {
    if (this.transferType === 0) {
      this.realCreditsForm.get('amount').setValidators(Validators.required);
      this.realCreditsForm.get('idMessageType').setValidators(Validators.required);
      this.realCreditsForm.get('superTosub').setValidators(Validators.required);
      this.realCreditsForm.get('amount').updateValueAndValidity();
      this.realCreditsForm.get('idMessageType').updateValueAndValidity();
      this.realCreditsForm.get('superTosub').updateValueAndValidity();
    }
    if (this.transferType === 2) {
      this.virtualCreditsForm
        .get('price')
        .setValidators([Validators.required, Validators.pattern(this.pricePattern), Validators.pattern(this.excludeZero)]);
      this.virtualCreditsForm.get('price').updateValueAndValidity();
    }
  }

  getCurrency() {
    return getCurrencySymbol();
  }

  createPricePerMessage(id?, unitPrice?, add?, idDestinationNation?): UntypedFormGroup {
    return this.formBuilder.group({
      idMessageType: [id],
      idDestinationNation: [idDestinationNation],
      unitPrice: [
        unitPrice,
        [Validators.pattern(this.pricePattern), Validators.pattern(this.excludeZero), checkPricePerMessage(this.price)]
      ],
      add: [add]
    });
  }

  atLeastOneChecked(): ValidatorFn {
    return function validate(formArray: UntypedFormArray) {
      let checked = 0;
      Object.keys(formArray.controls).forEach(key => {
        if (formArray.controls[key].value.add === true) {
          checked++;
        }
      });
      if (checked < 1) {
        return { atLeastOneChecked: true };
      } else {
        return null;
      }
    };
  }

  subscribeValueChanges() {
    for (const iterator of this.pricePerMessage.controls) {
      iterator
        .get('add')
        .valueChanges.pipe(takeUntil(this.unsubscribeAll))
        .subscribe(checked => {
          const options = {
            emitEvent: false
          };
          if (checked) {
            iterator
              .get('unitPrice')
              .setValidators([Validators.required, Validators.pattern(this.pricePattern), Validators.pattern(this.excludeZero)]);
            iterator.get('unitPrice').updateValueAndValidity(options);
          } else {
            // iterator.get('unitPrice').patchValue('');
            iterator.get('unitPrice').clearValidators();
            iterator.get('unitPrice').updateValueAndValidity(options);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  get pricePerMessage(): UntypedFormArray {
    return this.virtualCreditsForm.get('pricePerMessage') as UntypedFormArray;
  }

  onChange() {
    this.initPricePerMessage();
  }

  loadRateProfil() {
    this.superaccountService
      .loadRateProfileNew(this.selectedPricePlan)
      .pipe(
        map(data => {
          const newData = {
            amount: data.defaultAmount,
            pricePerMessage: []
          };
          newData.pricePerMessage = data.pricePerMessage.map(el => ({ ...el, unitPrice: el.cost }));
          return newData;
        })
      )
      .subscribe(plan => {
        this.virtualCreditsForm.controls.price.patchValue(plan.amount);
        for (const newValue of plan.pricePerMessage) {
          //search normal
          let findIndex = _.findIndex(this.pricePerMessage.value, {
            idMessageType: newValue.type,
            idDestinationNation: newValue.idDestinationNation
          });

          //search force user nation
          if (findIndex === -1 && !newValue.idDestinationNation) {
            findIndex = _.findIndex(this.pricePerMessage.value, {
              idMessageType: newValue.type,
              idDestinationNation: this.user.nation
            });
          }

          //search force user nation as null
          if (findIndex == -1 && newValue.idDestinationNation === this.user.nation) {
            findIndex = _.findIndex(this.pricePerMessage.value, { idMessageType: newValue.type, idDestinationNation: null });
          }

          if (findIndex == -1) {
            this.pricePerMessage.push(this.createPricePerMessage(newValue.type, newValue.unitPrice, true, newValue.idDestinationNation));
          } else {
            this.pricePerMessage
              .at(findIndex)
              .get('unitPrice')
              .patchValue(newValue.unitPrice);
            this.pricePerMessage
              .at(findIndex)
              .get('add')
              .patchValue(true);
          }
        }
        this.subscribeValueChanges();
        const sorted = this.superaccountService.sortPricePerNation(
          this.pricePerMessage.value,
          'idMessageType',
          'idDestinationNation',
          this.user.nation
        );
        this.virtualCreditsForm.get('pricePerMessage').patchValue(sorted);
      });
  }

  send() {
    if (this.transferType === 0) {
      this.subaccountService.transferCredits(this.realCreditsForm.value).subscribe(
        success => {
          this.atlasToast.success('settings_subaccounts_movecredit_success', null, 6000, this.toastContainer);
          this.primary.emit();
        },
        error => {
          this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        }
      );
    } else if (this.transferType === 2) {
      const body = {
        idSubaccountUser: this.virtualCreditsForm.get('idSubaccountUser').value,
        price: this.virtualCreditsForm.get('price').value,
        pricePerMessage: []
      };
      for (const iterator of this.pricePerMessage.controls) {
        if (iterator.get('add').value) {
          body.pricePerMessage = [
            ...body.pricePerMessage,
            {
              idMessageType: iterator.get('idMessageType').value,
              idDestinationNation: iterator.get('idDestinationNation').value,
              unitPrice: iterator.get('unitPrice').value
            }
          ];
        }
      }

      this.subaccountService.createPurchase(body).subscribe(
        success => {
          this.atlasToast.success('settings_subaccounts_movecredit_success', null, 6000, this.toastContainer);
          this.primary.emit();
        },
        error => {
          this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        }
      );
    }
  }

  onScrollToEnd() {
    this.scrollPage++;
    this.superaccountService.getListRatesProfile(this.scrollPage).subscribe(response => {
      if (response.data) {
        for (const iterator of response.data) {
          this.pricePlan = [
            ...this.pricePlan,
            {
              value: iterator.idRateProfile,
              label: iterator.name
            }
          ];
        }
      }
    });
  }

  findLabelNation(id) {
    const nation = _.find(this.nations, { id: id });
    if (nation) {
      if (id === this.user.nation) {
        return this.translateService.instant('credit_type_gp');
      }
      return nation.label;
    }
    return id + ' - unknow';
  }

  removePricePerNation(index) {
    this.pricePerMessage.removeAt(index);
    // this.sortPricePerNation();
    // this.editForm.markAsDirty();
  }

  selectNation(ev) {
    const isExist = _.find(this.pricePerMessage.value, { idDestinationNation: ev.id });
    if (!isExist) {
      this.pricePerMessage.push(this.createPricePerMessage('GP', '', false, ev.id));
      const sorted = this.superaccountService.sortPricePerNation(
        this.pricePerMessage.value,
        'idMessageType',
        'idDestinationNation',
        this.user.nation
      );
      this.virtualCreditsForm.get('pricePerMessage').patchValue(sorted);
    }
  }
}
